import { useState, useEffect } from 'react'
import { Shield, Building2, Users, BarChart3, Loader2 } from 'lucide-react'
import { listAllTenants, listTenantUsers, TenantSummary, TenantUser } from '../../../services/api'
import AdminOverview from './AdminOverview'
import AdminCompanyList from './AdminCompanyList'
import AdminCompanyDetail from './AdminCompanyDetail'
import AdminCompanyForm from './AdminCompanyForm'

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  editor: 'bg-green-100 text-green-700',
  viewer: 'bg-gray-100 text-gray-600',
}

type Tab = 'overview' | 'companies' | 'users'

interface AllUser extends TenantUser {
  company_name: string
  tenant_id: string
}

export default function AdminPageMain() {
  const [activeTab, setActiveTab] = useState<Tab>('companies')
  const [tenants, setTenants] = useState<TenantSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)

  // All Users tab state
  const [allUsers, setAllUsers] = useState<AllUser[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [userSearch, setUserSearch] = useState('')

  useEffect(() => {
    loadTenants()
  }, [])

  async function loadTenants() {
    try {
      setLoading(true)
      const data = await listAllTenants()
      setTenants(data.tenants || [])
    } catch (err) {
      console.error('Failed to load tenants:', err)
    } finally {
      setLoading(false)
    }
  }

  async function loadAllUsers() {
    setUsersLoading(true)
    try {
      const results = await Promise.allSettled(
        tenants.map(async (t) => {
          const data = await listTenantUsers(t.tenant_id)
          return (data.users || []).map((u) => ({
            ...u,
            company_name: t.name,
            tenant_id: t.tenant_id,
          }))
        })
      )
      const users: AllUser[] = results
        .filter((r): r is PromiseFulfilledResult<AllUser[]> => r.status === 'fulfilled')
        .flatMap((r) => r.value)
      setAllUsers(users)
    } finally {
      setUsersLoading(false)
    }
  }

  function handleTabChange(tab: Tab) {
    setActiveTab(tab)
    if (tab === 'users' && tenants.length > 0) {
      loadAllUsers()
    }
  }

  function handleSelectCompany(tenantId: string) {
    setSelectedTenantId(tenantId)
  }

  function handleNewCompany() {
    setShowCreateModal(true)
  }

  const selectedTenant = tenants.find((t) => t.tenant_id === selectedTenantId)

  const filteredUsers = allUsers.filter(
    (u) =>
      !userSearch ||
      u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.company_name.toLowerCase().includes(userSearch.toLowerCase())
  )

  const tabs = [
    { id: 'overview' as Tab, label: 'Overview', icon: BarChart3 },
    { id: 'companies' as Tab, label: 'Companies', icon: Building2, count: tenants.length },
    { id: 'users' as Tab, label: 'All Users', icon: Users },
  ]

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
        </div>
        <p className="text-gray-500 mt-1 text-sm">
          Manage companies, users, and platform settings
        </p>
      </div>

      {/* Tabs + Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:w-56 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-1.5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-amber-50 text-amber-900'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-amber-600' : ''}`} />
                  {tab.label}
                </div>
                {tab.count !== undefined && (
                  <span className="text-xs text-gray-400">{tab.count}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'overview' && (
            <AdminOverview
              tenants={tenants}
              onNewCompany={handleNewCompany}
              onSelectCompany={(id) => {
                setSelectedTenantId(id)
                setActiveTab('companies')
              }}
            />
          )}

          {activeTab === 'companies' && (
            <AdminCompanyList
              tenants={tenants}
              loading={loading}
              onSelectCompany={handleSelectCompany}
              onNewCompany={handleNewCompany}
            />
          )}

          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <input
                  type="text"
                  placeholder="Search users across all companies..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 outline-none"
                />
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {usersLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 text-gray-300 animate-spin" />
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-20 text-gray-400 text-sm">
                    {userSearch ? 'No users match your search' : 'No users found'}
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Email</th>
                        <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Company</th>
                        <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Role</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredUsers.map((u, i) => (
                        <tr
                          key={`${u.tenant_id}-${u.email}-${i}`}
                          onClick={() => handleSelectCompany(u.tenant_id)}
                          className="hover:bg-amber-50/30 cursor-pointer transition-colors"
                        >
                          <td className="px-5 py-3 text-sm text-gray-900">{u.email}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{u.company_name}</td>
                          <td className="px-5 py-3">
                            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}>
                              {u.role}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {!usersLoading && filteredUsers.length > 0 && (
                <p className="text-xs text-gray-400">
                  {filteredUsers.length} users across {tenants.length} companies
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Company Detail Slide-over */}
      {selectedTenant && (
        <AdminCompanyDetail
          tenant={selectedTenant}
          onClose={() => setSelectedTenantId(null)}
          onUpdated={loadTenants}
        />
      )}

      {/* Create Company Modal */}
      {showCreateModal && (
        <AdminCompanyForm
          onClose={() => setShowCreateModal(false)}
          onCreated={loadTenants}
        />
      )}
    </div>
  )
}
